import type { RegexFlag } from '@/lib/regex';

export type Lang = 'de' | 'en';

export interface Strings {
  langName: string;
  switchTo: string;
  nav: { privacy: string; imprint: string; allTools: string };
  hero: { title: string; subtitle: string; localBadge: string };
  ui: {
    pattern: string;
    flags: string;
    flagNames: Record<RegexFlag, string>;
    testText: string;
    result: string;
    matches: string;
    groups: string;
    empty: string;
    cheatsheet: string;
    cheats: { token: string; desc: string }[];
  };
  seo: {
    aboutTitle: string;
    aboutBody: string;
    faqTitle: string;
    faq: { q: string; a: string }[];
  };
  footer: { rights: string; noStorage: string };
}

const cheatsDe = [
  { token: '\\d', desc: 'eine Ziffer (0–9)' },
  { token: '\\w', desc: 'Wortzeichen (a–z, 0–9, _)' },
  { token: '\\s', desc: 'Leerraum' },
  { token: '.', desc: 'beliebiges Zeichen' },
  { token: '^ $', desc: 'Zeilenanfang / -ende' },
  { token: '* + ?', desc: '0+, 1+, optional' },
  { token: '{n,m}', desc: 'n bis m Wiederholungen' },
  { token: '[abc]', desc: 'Zeichenklasse' },
  { token: '(…)', desc: 'Gruppe' },
  { token: 'a|b', desc: 'a oder b' },
];

const cheatsEn = [
  { token: '\\d', desc: 'a digit (0–9)' },
  { token: '\\w', desc: 'word character (a–z, 0–9, _)' },
  { token: '\\s', desc: 'whitespace' },
  { token: '.', desc: 'any character' },
  { token: '^ $', desc: 'start / end of line' },
  { token: '* + ?', desc: '0+, 1+, optional' },
  { token: '{n,m}', desc: 'n to m repetitions' },
  { token: '[abc]', desc: 'character class' },
  { token: '(…)', desc: 'group' },
  { token: 'a|b', desc: 'a or b' },
];

const de: Strings = {
  langName: 'Deutsch',
  switchTo: 'English',
  nav: { privacy: 'Datenschutz', imprint: 'Impressum', allTools: 'Alle Tools' },
  hero: {
    title: 'Regex-Tester',
    subtitle:
      'Teste reguläre Ausdrücke live im Browser: Treffer-Hervorhebung, Gruppen und Flags. Nichts wird gesendet.',
    localBadge: '100 % lokal · kostenlos',
  },
  ui: {
    pattern: 'Muster (Pattern)',
    flags: 'Flags',
    flagNames: { g: 'global', i: 'Groß/Klein egal', m: 'mehrzeilig', s: 'dotall', u: 'Unicode', y: 'sticky' },
    testText: 'Testtext',
    result: 'Ergebnis',
    matches: 'Treffer',
    groups: 'Capture-Gruppen',
    empty: '(kein Text)',
    cheatsheet: 'Spickzettel',
    cheats: cheatsDe,
  },
  seo: {
    aboutTitle: 'Reguläre Ausdrücke sicher und lokal testen',
    aboutBody:
      'Dieser Tester wertet reguläre Ausdrücke ausschließlich lokal in deinem Browser aus. Muster und Testtext werden nicht an einen Server gesendet oder gespeichert.',
    faqTitle: 'Häufige Fragen',
    faq: [
      {
        q: 'Welche Regex-Syntax wird unterstützt?',
        a: 'Die JavaScript-RegExp-Engine deines Browsers, inklusive der Flags g, i, m, s, u und y.',
      },
      {
        q: 'Werden meine Daten gesendet?',
        a: 'Nein. Die Auswertung läuft vollständig lokal; nichts wird übertragen.',
      },
      {
        q: 'Warum werden alle Treffer angezeigt?',
        a: 'Zum Testen iterieren wir global über den Text. Mit dem Flag y (sticky) wird nur ab Position gesucht.',
      },
    ],
  },
  footer: {
    rights: 'Kostenlose Tools von ecosquirrel.',
    noStorage: 'Keine serverseitige Speicherung. Alles läuft lokal im Browser.',
  },
};

const en: Strings = {
  langName: 'English',
  switchTo: 'Deutsch',
  nav: { privacy: 'Privacy', imprint: 'Imprint', allTools: 'All tools' },
  hero: {
    title: 'Regex Tester',
    subtitle:
      'Test regular expressions live in your browser: match highlighting, groups and flags. Nothing is sent.',
    localBadge: '100% local · free',
  },
  ui: {
    pattern: 'Pattern',
    flags: 'Flags',
    flagNames: { g: 'global', i: 'ignore case', m: 'multiline', s: 'dotall', u: 'unicode', y: 'sticky' },
    testText: 'Test string',
    result: 'Result',
    matches: 'matches',
    groups: 'Capture groups',
    empty: '(no text)',
    cheatsheet: 'Cheat sheet',
    cheats: cheatsEn,
  },
  seo: {
    aboutTitle: 'Test regular expressions securely and locally',
    aboutBody:
      'This tester evaluates regular expressions exclusively in your browser. Pattern and test string are not sent to a server or stored.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        q: 'Which regex syntax is supported?',
        a: "Your browser's JavaScript RegExp engine, including the flags g, i, m, s, u and y.",
      },
      {
        q: 'Is my data sent anywhere?',
        a: 'No. Evaluation runs entirely locally; nothing is transmitted.',
      },
      {
        q: 'Why are all matches shown?',
        a: 'For testing we iterate globally over the text. With the y (sticky) flag, matching starts at a position.',
      },
    ],
  },
  footer: {
    rights: 'Free tools by ecosquirrel.',
    noStorage: 'No server-side storage. Everything runs locally in your browser.',
  },
};

export const STRINGS: Record<Lang, Strings> = { de, en };
